import { Negative, Observation, Series } from "./StudyApi";
import CancelableApi from "./CancelableApi";
import CancelableAxiosInstance from "./CancelableAxiosInstance";
import { Sentence } from "./TypedReportApi";

export interface LabelObsStudy {
  id: string;

  state: string;
  comment: string;

  series: Array<Series>;
  showNegatives: boolean;
  showAiRegions: boolean;
  enablingAiWaitSecond: number;
  negatives: Negative[];

  indication: string;
  observations: Array<Observation>;
  patientSex: string;
  patientAge: number;
  studyDescription: string;
  studyDate: string;
  labelingObsTypes: Array<string>;
  labelWorkflowId: string;
  labelBatchId: string;
  _aiHasBeenActivated?: boolean;
  secDuration?: number;
  tags: string[];
  workflowStudyTags: string[];
  workflowImageTags: string[];
}

export interface LabelTypedReport {
  id: string;
  rawText: string;
  sentences: Array<Sentence>;
}

export interface LabelPartParam {
  studyId: string;
  glmStudyId: string;

  imageId: string;
  imagePath: string;
  imageWidth: number;
  imageHeight: number;
  imageThumbnailPath: string;

  partIdx: number;
  offset: number;
  length: number;
  type: string;
  value: string;
  state: string;
  imageState: string;
  labelWorkflowId: string;
  labelBatchId: string;
  aiConfidence: number;
  aiValue: string;
}

export interface LabelWorkflowBatch {
  label?: string;
  labelBatchId: string;
  labelWorkflowId: string;
  labelWorkflowType?: string;
  studyCount?: number;
}

export interface LabelWorkflowStats {
  workflowLabel: string;
  from: string;
  to: string;
  obsStats: Array<ObsStat>;
}

export interface ObsStat {
  state: string;
  count: number;
}

export default class LabelizeApi extends CancelableApi {
  static with(axios: CancelableAxiosInstance) {
    return new LabelizeApi(axios);
  }

  newObservation(study: LabelObsStudy, obsType: string): Promise<Observation> {
    return this.client
      .get("/label-workflows/" + study.labelWorkflowId + "/new-observation", { params: { obsType: obsType } })
      .then((resp) => resp.data);
  }

  queryPartParamsByWorkflow(lwb: LabelWorkflowBatch): Promise<LabelPartParam[]> {
    return this.client
      .get("/label-workflows/" + lwb.labelWorkflowId + "/batches/" + lwb.labelBatchId + "/part-params", {
        params: {
          lbId: lwb.labelBatchId,
          lwId: lwb.labelWorkflowId,
        },
      })
      .then((resp) => resp.data);
  }

  savePartParam(labelPartParam: LabelPartParam): Promise<LabelPartParam> {
    return this.client
      .post(
        "/label-workflows/" +
          labelPartParam.labelWorkflowId +
          "/batches/" +
          labelPartParam.labelBatchId +
          "/part-params",
        labelPartParam
      )
      .then((resp) => resp.data);
  }

  findLabelWorkflowBatches(): Promise<[LabelWorkflowBatch]> {
    return this.client.get("/label-workflow-batches").then((resp) => resp.data);
  }

  queryLabelWorkflowStats() {
    return this.client.get("/label-workflow-stats").then((resp) => resp.data);
  }

  getStudy(lwb: LabelWorkflowBatch, current?: LabelObsStudy): Promise<LabelObsStudy> {
    let url = "/label-workflows/" + lwb.labelWorkflowId + "/batches/" + lwb.labelBatchId + "/study/";
    if (current) url += "?exclude=" + current.id;
    return this.client.get(url).then((resp) => resp.data);
  }

  saveStudy(study: LabelObsStudy): Promise<LabelObsStudy> {
    return this.client
      .post(
        "/label-workflows/" + study.labelWorkflowId + "/batches/" + study.labelBatchId + "/study/" + study.id,
        study
      )
      .then((resp) => resp.data);
  }

  previousStudy(study: LabelObsStudy): Promise<LabelObsStudy> {
    return this.client
      .get(
        "/label-workflows/" +
          study.labelWorkflowId +
          "/batches/" +
          study.labelBatchId +
          "/previous-study/" +
          study.id +
          "?lbId=" +
          study.labelBatchId
      )
      .then((resp) => resp.data);
  }

  getLabelTypeReportsByStudyId(studyId: string): Promise<Array<LabelTypedReport>> {
    return this.client.get("/labelize/study/" + studyId + "/typed-reports").then((resp) => resp.data);
  }
}
